import format from 'date-fns/format';
import parse from 'date-fns/parse';
import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  SvgIconButton,
  Button,
  Typography,
  Switch,
  Skeleton,
  Box,
  Drawer,
  FlexBox,
  InputField,
  Card,
  SvgIcon,
  color
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import {
  actions as kioskActions,
  selectors as kioskSelectors,
  Kiosk,
  KioskSchedule,
  KioskScheduleEntry,
  DayNumber,
  UpdateKioskSchedulePayload
} from 'store/kiosks';
import { formatMilitaryTime } from 'utils/stringFormat';
import { validateTime } from 'utils/validation';

export interface KioskScheduleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  kiosk: Kiosk;
}

interface KioskScheduleFormEntry extends Omit<KioskScheduleEntry<string>, 'id'> {
  online: boolean;
  name: string;
}

const timeStringToMilitaryTime = (time: string): number | undefined => {
  const withoutSpaces = time.replace(' ', '');
  if (!withoutSpaces) return undefined;

  const date = parse(withoutSpaces, 'h:mma', new Date());
  if (isNaN(date.getTime())) throw new Error('Invalid time');

  const formatted = format(date, 'HHmm');

  return parseInt(formatted, 10);
};

export const KioskScheduleDrawer = (props: KioskScheduleDrawerProps) => {
  const { isOpen, onClose, kiosk } = props;
  const { updateKioskSchedule, resetAction } = useActions({
    updateKioskSchedule: kioskActions.updateKioskSchedule,
    resetAction: kioskActions.resetAction
  });

  const { success, pending } = useSelector(kioskSelectors.selectAction);
  const kioskSchedule = useSelector(kioskSelectors.selectKioskSchedule);

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const getForm = (kioskScheduleData: KioskSchedule): KioskScheduleFormEntry[] =>
    days.map((d, idx) => {
      const kioskScheduleDay = kioskScheduleData.find((sd) => sd.day === idx);

      return {
        name: d,
        online: !!kioskScheduleDay,
        kioskId: kioskScheduleDay ? kioskScheduleDay.kioskId : '',
        startTime: kioskScheduleDay ? formatMilitaryTime(kioskScheduleDay.startTime) : '',
        endTime: kioskScheduleDay ? formatMilitaryTime(kioskScheduleDay.endTime) : '',
        day: idx as DayNumber
      };
    });

  let schedule: KioskScheduleFormEntry[] = getForm(kioskSchedule);
  const [form, setForm] = useState(schedule);
  const [validation, setValidation] = useState<{ [key: string]: string }[]>(
    form.map(() => ({
      startTime: 'valid',
      endTime: 'valid'
    }))
  );

  useEffect(() => {
    if (isOpen && success) {
      onClose();
      resetAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, success]);

  useEffect(() => {
    const newSchedule = getForm(kioskSchedule);
    setForm(newSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskSchedule]);

  const onSwitchDay = (dayIdx: number, online: boolean): void => {
    if (!online) {
      schedule = schedule.filter((s) => s.day !== dayIdx);
    }

    setForm(
      form.map((day, idx) => ({
        ...day,
        online: idx === dayIdx ? online : day.online
      }))
    );
  };

  const validationSchema = yup.object().shape({
    startTime: validateTime(),
    endTime: validateTime()
  });

  const validate =
    (dayIdx: number, key: keyof KioskScheduleFormEntry) => async (value: string | number | undefined) => {
      let invalid = false;
      if (value && value.toString().trim().length) {
        const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);

        invalid = !!(result && result.message);
      }

      setValidation(
        validation.map((v, idx) => ({
          ...v,
          [key]: idx === dayIdx && invalid ? 'invalid' : 'valid'
        }))
      );
    };

  const onInputBlur = (dayIdx: number, key: keyof KioskScheduleFormEntry, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setForm(
      form.map((day, idx) => ({
        ...day,
        [key]: idx === dayIdx ? e.target.value.trim() : day[key]
      }))
    );
  };

  const onInputChange = (dayIdx: number, key: keyof KioskScheduleFormEntry, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    // if (validation[key] === INVALID) setValidation({ ...validation, [key]: VALID });

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm(
      form.map((day, idx) => ({
        ...day,
        [key]: idx === dayIdx ? e.target.value : day[key]
      }))
    );
  };

  const anyInvalidTimes = useMemo(
    () => validation.some((v) => Object.values(v).some((value) => value === 'invalid')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, validation]
  );

  const anyEmptyTimes = useMemo(
    () => form.filter((f) => f.online).some((f) => !f.startTime || !f.endTime),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, validation]
  );

  const buttonDisabled = anyInvalidTimes || anyEmptyTimes;

  const onCloseSchedule = () => {
    onClose();
    schedule = getForm(kioskSchedule);
    setForm(schedule);
    // clear date format invalid error showing in textinput
    setValidation(
      form.map(() => ({
        startTime: 'valid',
        endTime: 'valid'
      }))
    );
  };
  const onSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    let payload: UpdateKioskSchedulePayload;

    try {
      payload = {
        kioskId: kiosk.id,
        schedule: form
          .filter((f) => f.online)
          .map((f) => {
            if (!f.startTime || !f.endTime) {
              throw new Error('An online day requires a start and end time');
            }

            return {
              day: f.day,
              startTime: timeStringToMilitaryTime(f.startTime),
              endTime: timeStringToMilitaryTime(f.endTime)
            };
          })
      };
    } catch (err) {
      return;
    }

    updateKioskSchedule(payload);
  };

  return (
    <Drawer open={isOpen} onClose={onClose} hasBorderRadius>
      <FlexBox direction="column" width={720} bg="white">
        <FlexBox align="center" justify="between" padding="6 6 2 6">
          <Typography fontSize={32} fontWeight={600}>
            Kiosk Schedule
          </Typography>
          <SvgIconButton appearance="transparent" onClick={onCloseSchedule} data-testid="kiosk-schedule-x">
            <SvgIcon name="exit_circle" size={40} />
          </SvgIconButton>
        </FlexBox>
        <Box padding="2 6" fullWidth bg="light-gray">
          <Typography fontWeight={600} fontSize={16}>
            {kiosk.name}
          </Typography>
        </Box>
        <FlexBox direction="column">
          {pending ? (
            <FlexBox gutter={4} direction="column" padding="4 6" overflow="hidden">
              <Skeleton width={600} />
              <Skeleton width={600} />
              <Skeleton width={600} />
              <Skeleton width={600} />
              <Skeleton width={600} />
              <Skeleton width={600} />
              <Skeleton width={600} />
            </FlexBox>
          ) : (
            <form>
              <FlexBox direction="column" padding="0 8">
                {form.map((day, idx) => (
                  <FlexBox key={`kiosk-schedule-${idx}`} sx={{ flexDirection: 'column' }}>
                    <FlexBox padding="4 0" justify="start" flexGrow={1}>
                      <FlexBox width={140} align="center">
                        <Typography fontSize={16} fontWeight={600}>
                          {days[idx]}
                        </Typography>
                      </FlexBox>
                      <FlexBox margin="0 6 0 0" sx={{ minHeight: '38px', alignItems: 'center' }}>
                        <Switch
                          checked={day.online}
                          onChange={(_e, checked) => onSwitchDay(idx, checked)}
                          data-testid={`${day.name.toLowerCase()}-switch`}
                          checkedLabel="ACTIVE"
                          uncheckedLabel="INACTIVE"
                        />
                      </FlexBox>
                      {day.online && (
                        <>
                          <InputField
                            size="small"
                            sx={{
                              borderRadius: '8px',
                              width: '100px',
                              height: '36px',
                              fontSize: '16px',
                              fontWeight: 400,
                              padding: '0px 12px'
                            }}
                            value={day.startTime}
                            onChange={(e) => onInputChange(idx, 'startTime', e)}
                            onBlur={(e) => onInputBlur(idx, 'startTime', e)}
                            helperText={validate(idx, 'startTime')}
                            error={validation[idx].startTime === 'invalid'}
                            data-testid={`${day.name.toLowerCase()}-starttime`}
                            placeholder="hh:mm AM/PM"
                          />
                          <FlexBox width={80} align="center" justify="center">
                            <Typography align="center" fontSize={16} sx={{ width: '100%' }}>
                              To
                            </Typography>
                          </FlexBox>
                          <InputField
                            size="small"
                            sx={{
                              borderRadius: '8px',
                              width: '100px',
                              height: '36px',
                              fontSize: '16px',
                              fontWeight: 400,
                              padding: '0px 12px'
                            }}
                            value={day.endTime}
                            onChange={(e) => onInputChange(idx, 'endTime', e)}
                            onBlur={(e) => onInputBlur(idx, 'endTime', e)}
                            helperText={validate(idx, 'endTime')}
                            error={validation[idx].endTime === 'invalid'}
                            data-testid={`${day.name.toLowerCase()}-endtime`}
                            placeholder="hh:mm AM/PM"
                          />
                        </>
                      )}
                    </FlexBox>
                    <Card.Divider color={color.BORDER} />
                  </FlexBox>
                ))}
                <Box margin="1 0 0">
                  <Typography color={color.RED} fontSize={14}>
                    {anyInvalidTimes ? 'Time formats must be h:mm am/pm' : <br />}
                  </Typography>
                </Box>
                <Box flexOverride="none" margin="1 0 0" padding="6 0">
                  <Button customSize="large" width={334} type="submit" disabled={buttonDisabled} onClick={onSaveClick}>
                    Save
                  </Button>
                </Box>
              </FlexBox>
            </form>
          )}
        </FlexBox>
      </FlexBox>
    </Drawer>
  );
};
